/**
 * CivilDate (proto google.type.Date 相当の plain object) を編集する
 * design (flatpickr) 相当の compact calendar popup 入力。 全画面横断で使う
 * 共通 date input (元は 広域マスタ 内 helper、 2026-08-05 に `common/` へ昇格)。
 *
 * popup の見た目は DS `DatePicker` に揃える (padding 24px・角丸 radii2xl・上部の
 * 「選択中の日付」 欄・`« ‹ 年月 › »`・32px の丸の日セル)。 React mock `DateInput` /
 * 8080 (2026-10-08 に追随) と同じ (`design/ui-feasibility/components/datepicker.md`)。
 * ただし DS `DatePicker` 自体は使わず、 特異日の装飾を持てる自前の [[DayGrid]] を
 * DIY popover に置く (DS には日ごとの装飾フックが無い。 DayGrid の docstring 参照)。
 *
 * popup は **`document.body` へ portal** し `position: fixed` で anchor の rect から
 * 位置を出す。 anchor の子として `position: absolute` に置くと、 スクロールする祖先
 * (フィルタサイドバーの `overflowY: auto` + `maxHeight`、 table wrap の
 * `overflowX: auto` 等) にクリップされてカレンダーが押せなくなるため
 * (2026-08-13 デザイナー指摘)。 DS `Select` も同じ理由で
 * `menuPortalTarget={document.body}` + `zIndex: 1500` を採っており、 それに揃える。
 *
 * 責務:
 * - anchor input: readonly text + calendar icon + optional clear (X) button
 * - popup: 外側 click / Esc で close、 選択で onChange 発火
 * - a11y / test 互換: visually-hidden native `<input type="date">` を id 付きで
 *   置き、 label htmlFor 経由の accessible name / `fireEvent.change` に対応
 *
 * 詳細な採用理由 / 落とし穴 (chakra v3.29 Popover の型崩れ 等) は
 * `src/pages/AdminOcctoMaster/tabs/_common/components/TopLevelValidPeriodFields.tsx`
 * のヘッダコメント参照 (2026-08-04 抽出)。
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Box, Text, chakra } from '@enechain/ecloud-designsystem'
import { FaCalendarRegular, FaXmarkRegular } from '@enechain/ecloud-icons'

import {
  type DayCellState,
  DayGrid,
  type SingularDayLabel,
  WEEKDAY_KEYS,
} from '~/components/common/DayGrid'
import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import { iconSize } from '~/constants/iconSize'
import {
  type CivilDate,
  civilDateToInputValue,
  compareCivilDate,
  parseDateInputValue,
} from '~/models/civilDate'
import { formatDateDisplay } from '~/models/dateTime'

/**
 * 閉じた入力欄の幅。 呼び出し側が固定幅の枠で包む時はこの値を使う
 * (画面ごとに 138px / 150px / 176px と散っていた値の置き場)。
 *
 * React mock `DateInput` の既定幅 (212px、 ja / en 共通) に揃える。 値に曜日を
 * 付けた (2026-10-20 デザイナー指示「日付表示時に曜日も表示したい」) ので、
 * **最長の表示値 = en の `2026/12/30 (Wed)`** が収まる必要がある。 14px で 133px
 * (app の Hiragino 始まりの書体、 Windows の Meiryo 相当の Verdana とも Chromium で
 * 実測。 Arial なら 112px だが app の書体ではない。 ja の `(水)` は 116px)。
 * 左余白 34px (カレンダー icon) + 右余白 8px + 枠 2px で 177px、 × ボタン付き
 * (`hasClear`、 右余白 [[CLEAR_BUTTON_INLINE_END]]) で 201px で、 どちらも収まる。
 */
export const COMPACT_DATE_FIELD_WIDTH = '212px'

/**
 * × ボタンを出さない閉じた欄を、 値が収まるところまで詰めた幅。 上の実測の 177px
 * (最長の en `2026/12/30 (Wed)` + 左余白 34px + 右余白 8px + 枠 2px) に 3px の余裕を足した値。
 * 前後の送りと 1 組で狭いマスに並べる時に使う (時間前入札の受渡日。 2026-10-26 デザイナー
 * 「`>` ボタンは datepicker の右に必ず配置したい。 picker の width をコンパクトにして配置できるように」)。
 */
export const COMPACT_DATE_FIELD_TIGHT_WIDTH = '180px'

/** × ボタンを出している時の右余白 (右端 4px + ボタン 24px + 文字との間 4px)。 */
const CLEAR_BUTTON_INLINE_END = '32px'

function civilDateToJsDate(d: CivilDate): Date {
  return new Date(d.year, d.month - 1, d.day)
}

function jsDateToCivilDate(d: Date): CivilDate {
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() }
}

type CompactCalendarPopoverProps = {
  value?: Date
  onChange: (d: Date) => void
  anchor: React.ReactNode
  /** true のとき popup を開かない (edit モーダルの immutable フィールド用)。 */
  disabled?: boolean
  /** これより後の日付を選択不可にする (issue #6361)。境界は inclusive。 */
  maxDate?: Date
  /** これより前の日付を DS `Calendar` 側で選択不可にする (`bidding/intraday-bidding.md` AC-BIN-000a)。 */
  minDate?: Date
  /**
   * 日付キー (`YYYY-MM-DD`) → その日の特異日。 **opt-in** で、渡さなければ
   * 装飾しない ([[DayGrid]] 参照)。
   */
  singularDaysByDateKey?: ReadonlyMap<string, SingularDayLabel[]>
  /** 日ごとの選択可否と説明 ([[DayGrid]] の `dayState`)。 */
  dayState?: (d: Date) => DayCellState | undefined
  /** 表示月の通知 ([[DayGrid]] の `onViewMonthChange`)。 */
  onViewMonthChange?: (year: number, month: number) => void
  /** popup 上部の「選択中の日付」 欄に出す文言 (未選択なら入力書式のヒント)。 */
  selectedLabel: string
  /** `selectedLabel` が未選択のヒントか (文字色を落とす)。 */
  isSelectedLabelPlaceholder: boolean
}

/**
 * popup の高さの概算 (DS `DatePicker` 相当: padding 24px × 2 + 日付欄 40px +
 * 送り 32px + 曜日 + 6 週 × 32px + 間隔)。 実寸が取れる時は実寸を使い、
 * これは描画前の 1 回目だけに使う。
 */
const POPUP_ESTIMATED_HEIGHT = 470
const POPUP_GAP = 4
/** popup を画面の端から離す余白。 */
const VIEWPORT_MARGIN = 8

const CompactCalendarPopover = ({
  singularDaysByDateKey,
  dayState,
  onViewMonthChange,
  selectedLabel,
  isSelectedLabelPlaceholder,
  value,
  onChange,
  anchor,
  disabled = false,
  maxDate,
  minDate,
}: CompactCalendarPopoverProps): React.ReactElement => {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  })
  const wrapRef = useRef<HTMLDivElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)

  // anchor の viewport 座標から popup 位置を決める (`position: fixed` 基準)。
  // 下に入りきらない時だけ上へ反転し、 右端からはみ出す時は左へ寄せる。
  // 高さ・幅は描画済みの popup の実寸を使う (DS `DatePicker` 相当の寸法にして
  // 固定の概算値ではずれやすくなったため)。
  const updatePosition = useCallback((): void => {
    const rect = wrapRef.current?.getBoundingClientRect()
    if (rect === undefined) return
    const measuredHeight = popupRef.current?.offsetHeight ?? 0
    const height = measuredHeight > 0 ? measuredHeight : POPUP_ESTIMATED_HEIGHT
    const width = popupRef.current?.offsetWidth ?? 0
    const flipUp =
      rect.bottom + POPUP_GAP + height > window.innerHeight &&
      rect.top - POPUP_GAP - height > 0
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN
    setPosition({
      left: Math.max(VIEWPORT_MARGIN, Math.min(rect.left, maxLeft)),
      top: flipUp ? rect.top - POPUP_GAP - height : rect.bottom + POPUP_GAP,
    })
  }, [])

  // 描画前に位置を確定させ、 左上 (0,0) で 1 フレーム見えるのを防ぐ。
  useLayoutEffect(() => {
    if (open) updatePosition()
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) return undefined
    const handlePointerDown = (e: MouseEvent): void => {
      const target = e.target as Node
      // popup は portal 先 (body 直下) にあるため wrapRef には含まれない。
      // anchor 側と popup 側の両方を「内側」 として扱う。
      if (wrapRef.current?.contains(target) === true) return
      if (popupRef.current?.contains(target) === true) return
      setOpen(false)
    }
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    const handleReposition = (): void => updatePosition()
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    // capture: true で**祖先のスクロールコンテナ** (サイドバー等) の scroll も拾う。
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [open, updatePosition])

  return (
    <Box ref={wrapRef} position="relative" width="full">
      <Box
        onClick={() => {
          if (disabled) return
          setOpen((v) => !v)
        }}
      >
        {anchor}
      </Box>
      {open &&
        !disabled &&
        // **portal 必須**: popup を anchor の子として `position: absolute` で置くと、
        // スクロールする祖先 (48点データのフィルタサイドバー `overflowY="auto"` +
        // `maxHeight`、 取引フォームの leg テーブル `overflowX="auto"` 等) に
        // クリップされ、 カレンダーの大半が見えず日付を押せなくなる
        // (2026-08-13 デザイナー指摘)。 DS `Select` が同じ問題を
        // `menuPortalTarget={document.body}` + `zIndex: 1500` で解いているので
        // 揃える (`~/components/common/filters/AreaFilter.tsx` 等)。
        createPortal(
          <Box
            ref={popupRef}
            position="fixed"
            zIndex={1500}
            // DS `Modal` (= ark Dialog) は開いている間 `<body>` に
            // **`inert` + inline の `pointer-events: none`** を付ける
            // (`~/components/common/FormSelect` に 2026-08-06 の実測記録あり)。
            // body へ portal した本 popup はこれを継承するため、 モーダル内
            // (広域マスタの有効期間など) で日付をクリックできなくなる。
            // 子側で `auto` に戻す (DsSelect / MultiSelect も同じ回避をしている)。
            top={`${position.top}px`}
            left={`${position.left}px`}
            flexDirection="column"
            gap={4}
            display="flex"
            // 枠は DS `DatePicker` の popover と同じ (padding 24px・角丸 radii2xl・
            // 枠 Border.lowEmphasis)。 React mock `DateInput` / 8080 も同じ
            // (`design/ui-feasibility/components/datepicker.md`)。 影は DS が `base` と
            // 書いているが、 DS の影トークンは `shadBase` 等で `base` は解決されず無影に
            // なる。 DS が意図した値の `shadBase` を指定する
            padding={6}
            borderWidth="1px"
            borderColor="Border.lowEmphasis"
            borderRadius="radii2xl"
            boxShadow="shadBase"
            pointerEvents="auto"
            backgroundColor="Surface.primary"
          >
            {/* DS `DatePicker` の DateDisplayLabel と同じ「選択中の日付」 欄 */}
            <Box
              alignItems="center"
              gap={2}
              display="flex"
              minHeight="40px"
              paddingStart={3}
              paddingEnd={4}
              borderWidth="1px"
              borderColor="Border.midEmphasis"
              borderRadius="radiiBase"
            >
              <Box as="span" display="inline-flex" color="Object.lowEmphasis">
                <FaCalendarRegular boxSize={iconSize.labeledButton} />
              </Box>
              <Text
                textStyle="textMd"
                color={
                  isSelectedLabelPlaceholder
                    ? 'Text.lowEmphasis'
                    : 'Text.highEmphasis'
                }
              >
                {selectedLabel}
              </Text>
            </Box>
            {/* DS `Calendar` ではなく自前の [[DayGrid]] を使う。 spec
                `shared-components.md` §8 (AC-SC-027/028) が求める特異日マーカーを
                DS が実現できない (日付ごとの装飾フックが無い。 0.17.10 でも
                `CalendarProps` は 0.14.2 と同一、 DS 要望 #1222 は無反応) ため。
                詳細な理由は DayGrid の docstring 参照。 */}
            <DayGrid
              dayState={dayState}
              maxDate={maxDate}
              minDate={minDate}
              onChange={(d) => {
                onChange(d)
                setOpen(false)
              }}
              onViewMonthChange={onViewMonthChange}
              singularDaysByDateKey={singularDaysByDateKey}
              value={value}
            />
          </Box>,
          document.body,
        )}
    </Box>
  )
}

type DateAnchorInputProps = {
  id: string
  'aria-label'?: string
  /** 閉じた欄に出す値 (`YYYY/MM/DD (曜)`)。 未入力なら空文字。 */
  displayValue: string
  isoValue: string
  /** 未入力時に出す入力書式のヒント。 */
  placeholder: string
  onNativeChange: (raw: string) => void
  hasError?: boolean
  hasClear?: boolean
  onClear?: () => void
  /** clear ボタンの読み上げ名と tooltip (`common:action.clear`)。 */
  clearLabel: string
  /** true のとき、 native input を disabled 化し anchor を greyed-out にする。 */
  disabled?: boolean
  /** native `<input type="date">` の `max` 属性 (ISO 文字列)。 未指定なら無制限。 */
  max?: string
  /** native `<input type="date">` の `min` 属性 (ISO 文字列)。 未指定なら無制限。 */
  min?: string
}

const DateAnchorInput = ({
  id,
  'aria-label': ariaLabel,
  displayValue,
  isoValue,
  placeholder,
  onNativeChange,
  hasError = false,
  hasClear = false,
  onClear,
  clearLabel,
  disabled = false,
  max,
  min,
}: DateAnchorInputProps): React.ReactElement => {
  const showsClear =
    !disabled && hasClear && displayValue !== '' && onClear !== undefined
  return (
    <Box
      position="relative"
      width="full"
      // UI 辞書 dictionary/components.md 素の input 行 / components/datepicker.md: 角丸 md (6px)、
      // フォーカスは枠色を変えず 3px の `Focus.clarity` リングだけ (DS `Input` の `_focus` と同じ)
      borderRadius="md"
      _focusWithin={
        disabled ? undefined : { boxShadow: '0 0 0 3px {colors.Focus.clarity}' }
      }
    >
      {/* Visually-hidden native <input type="date"> は keyboard 経路の入口を担う
        (Tab で focus 到達 → YYYY-MM-DD を type、 または browser 既定の date
        popup を Enter/Space 等で開く)。 focus 到達時は親 Box の _focusWithin
        outline が visible の text input 領域に描画される。 tabIndex を -1 に
        しないこと (a11y: 全 8 tab の validFrom/validTo が keyboard 到達不能に
        なる regression 防止)。 */}
      <chakra.input
        aria-label={ariaLabel}
        css={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
          '&::-webkit-calendar-picker-indicator': { display: 'none' },
        }}
        disabled={disabled}
        id={id}
        max={max}
        min={min}
        onChange={(e) => {
          onNativeChange(e.target.value)
        }}
        type="date"
        value={isoValue}
      />
      <Box
        as="span"
        position="absolute"
        zIndex={1}
        top="50%"
        left="8px"
        alignItems="center"
        display="inline-flex"
        // UI 辞書 components/datepicker.md「左にカレンダー icon 16px (`Text.lowEmphasis`)」
        color="Text.lowEmphasis"
        transform="translateY(-50%)"
        pointerEvents="none"
      >
        <FaCalendarRegular boxSize={iconSize.gridInline} />
      </Box>
      <chakra.input
        width="100%"
        height="40px"
        color={disabled ? 'Text.midEmphasis' : 'Text.highEmphasis'}
        fontSize="md"
        opacity={disabled ? 0.6 : 1}
        borderWidth="1px"
        borderColor={hasError ? 'Border.negativeMid' : 'Border.midEmphasis'}
        borderRadius="md"
        // placeholder の色は globalCss の `input::placeholder` (Text.disabled) に任せる
        cursor={disabled ? 'not-allowed' : 'pointer'}
        aria-hidden="true"
        backgroundColor={
          disabled
            ? 'Surface.secondary'
            : hasError
              ? 'Surface.negativeLow'
              : 'Surface.primary'
        }
        // × ボタンを出している時は値がその下に潜らないよう右余白を取る
        paddingInline={`34px ${showsClear ? CLEAR_BUTTON_INLINE_END : '8px'}`}
        // 未入力時は入力書式を示す。 design 8080 は全ページの `input[type="date"]` を
        // flatpickr に昇格させ、 altInput に `例：2024/04/01` (en は `e.g. 2024/04/01`) を
        // 当てている (`js/datepicker-init.js` の
        // `PH = _en ? 'e.g. 2024/04/01' : '例：2024/04/01'` を 2026-08-06 に実測)。
        // 本 component は表示を自前の read-only text input で描くため、同じ文言を
        // placeholder で再現する。
        placeholder={placeholder}
        readOnly
        tabIndex={-1}
        type="text"
        value={displayValue}
      />
      {showsClear && (
        <Box
          position="absolute"
          zIndex={2}
          top="50%"
          right="4px"
          transform="translateY(-50%)"
        >
          {/* アイコンだけでは何のボタンか見て分からないので、 読み上げ名と同じ文言の Tooltip を出す (IconOnlyButton が持つ) */}
          <IconOnlyButton
            label={clearLabel}
            onClick={(e) => {
              e.stopPropagation()
              onClear()
            }}
            size="xs"
            variant="ghost"
          >
            <FaXmarkRegular boxSize={iconSize.gridInline} />
          </IconOnlyButton>
        </Box>
      )}
    </Box>
  )
}

type Props = {
  /** htmlFor / id (label 経由の accessible name + test の `fireEvent.change` 入口)。 */
  id: string
  /**
   * accessible name を `<label htmlFor>` ではなく直接与えたい時に使う。
   *
   * 呼び出し側が**既に可視ラベルを描いている**場合、 visually-hidden な label を
   * 足すと同じ文言のテキストノードが 2 つになり、 `getByText` が複数マッチする
   * (2026-08-13 に同時同量の「表示日」で実際に発生)。 その場合はこちらを使う。
   */
  'aria-label'?: string
  /** 現在値。 `undefined` は未入力。 */
  value: CivilDate | undefined
  /**
   * 値変更 handler。
   * clear ボタン (`hasClear` 付き) を押した時、 または native input を
   * キーボードで空にした時 (`hasClear` の有無を問わない) に undefined で呼ばれる。
   * 呼び出し側で空値を許容しない場合は `d !== undefined` を判定すること。
   */
  onChange: (d: CivilDate | undefined) => void
  /** 赤枠 + 赤背景で invalid state を可視化する (design `.ecl-input.is-error`)。 */
  hasError?: boolean
  /**
   * true のとき、 値がある時のみ右端に × clear ボタンを表示する (validTo 用)。
   *
   * このフラグは **× ボタンの表示可否のみ**を制御する。 native (visually-hidden)
   * `<input type="date">` をキーボードで空にした時に `onChange(undefined)` を
   * 呼ぶかどうかとは無関係 — それは常に呼ぶ (raw `<input type="date">` が
   * 空文字をそのまま onChange へ渡していた旧挙動と同じ)。 呼び出し側で空値を
   * 無視したい場合は `onChange` 内で `d !== undefined` を判定すること
   * (PR #5323 レビュー指摘: hasClear 無しでは空欄化そのものを onChange へ
   * 伝えない実装だったため、 フィルタ解除・必須項目の再入力ができない
   * 回帰があった。 2026-08-13 修正)。
   */
  hasClear?: boolean
  /**
   * true のとき、 入力とカレンダー popup を完全に無効化する
   * (edit modal で日付が immutable な項目 = 特異日編集の日付フィールド等)。
   */
  disabled?: boolean
  /**
   * これより後の日付を選択不可にする (issue #6361: 同時同量の未来日ガード)。
   * カレンダー popup (DS `Calendar` の `maxDate`) と native `<input type="date">`
   * の `max` 属性の両方に配線する。native input は `max` があってもキーボード
   * 直接入力の change イベントは超過値でも発火するため、超過値は max へ
   * クランプして onChange する (design/js/hatsu-plant-data.js の参照実装と同じ
   * ガード)。省略時は従来どおり無制限。
   *
   * これは**入力 3 経路 (翌日ボタン / popup / 手入力) だけのガード**であり、
   * `value` 自体の不変条件ではない。呼び出し側が controlled な `value` に
   * max 超過の値を直接渡せば、そのまま表示される (この component は渡された
   * `value` を検証・クランプしない)。
   */
  max?: CivilDate
  /**
   * これより前の日付を選択不可にする (`bidding/intraday-bidding.md` AC-BIN-000a:
   * 入札追加タブの受渡日は過去日選択不可)。 `max` と対称のガードで、 カレンダー
   * popup (DS `Calendar` の `minDate`) と native `<input type="date">` の `min`
   * 属性の両方に配線し、 手入力経路の範囲外値は min へクランプして onChange する。
   * 省略時は従来どおり無制限。
   *
   * `max` と同じく**入力 3 経路だけのガード**であり、 呼び出し側が controlled な
   * `value` に min 未満の値を直接渡せば、 そのまま表示される。
   */
  min?: CivilDate
  /**
   * 日付キー (`YYYY-MM-DD`) → その日の特異日。 **opt-in** で、渡さなければ
   * カレンダーを装飾しない。
   *
   * 全画面に一斉に出さないのは、特異日が**エリア限定でありうる**ため
   * (`SingularDayRow.areaCodes`)。 どのエリアで絞るかはエリア文脈を持つ
   * 呼び出し元しか知らないので、解決済みの Map を渡してもらう。
   * 同じ日に複数載りうる (全エリア共通 + エリア限定の併存) ため配列で受ける。
   */
  singularDaysByDateKey?: ReadonlyMap<string, SingularDayLabel[]>
  /**
   * 日ごとの選択可否と説明。 **opt-in**。 disabled の日は popup で押しても、
   * native input に手入力しても `onChange` を呼ばない (`min` / `max` と違い
   * 寄せる先の日が無いので捨てる)。 hint は popup のその日の tooltip に出す。
   */
  dayState?: (d: Date) => DayCellState | undefined
  /**
   * popup の表示月が決まった時 (開いた時と月・年送りのたび) に呼ぶ。 `month` は
   * 1 始まり。 表示中の日の `dayState` を問い合わせる呼び出し元が使う。
   */
  onViewMonthChange?: (year: number, month: number) => void
}

/**
 * CivilDate 用の Flatpickr 相当 compact calendar field。 CompactCalendarPopover と
 * DateAnchorInput を組み合わせ、 CivilDate ⇄ JS Date の変換を吸収する。
 */
export const CompactCivilDateField = ({
  id,
  value,
  onChange,
  hasError = false,
  hasClear = false,
  disabled = false,
  'aria-label': ariaLabel,
  max,
  min,
  singularDaysByDateKey,
  dayState,
  onViewMonthChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const jsDate = value != null ? civilDateToJsDate(value) : undefined
  const displayValue = jsDate != null ? formatDateDisplay(jsDate) : ''
  // 閉じた欄だけ曜日を付ける (`2026/10/20 (火)` / `2026/10/20 (Tue)`)。 表示専用で、
  // 値 (`value`)・手入力 (native input の `YYYY-MM-DD`)・onChange に渡す値は変えない。
  // popup 上部の「選択中の日付」 欄は DS `DatePicker` の DateDisplayLabel に揃えて
  // 曜日を付けない (React mock `DateInput` も DS の欄のまま)。
  const anchorDisplayValue =
    jsDate != null
      ? t('common:date.withWeekday', {
          date: displayValue,
          weekday: t(
            `common:date.calendar.weekday.${WEEKDAY_KEYS[jsDate.getDay()]}`,
          ),
        })
      : ''
  const isoValue = value != null ? civilDateToInputValue(value) : ''
  const maxJsDate = max != null ? civilDateToJsDate(max) : undefined
  const maxIsoValue = max != null ? civilDateToInputValue(max) : undefined
  const minJsDate = min != null ? civilDateToJsDate(min) : undefined
  const minIsoValue = min != null ? civilDateToInputValue(min) : undefined
  const placeholder = t('common:date.inputPlaceholder')

  return (
    <CompactCalendarPopover
      anchor={
        <DateAnchorInput
          aria-label={ariaLabel}
          clearLabel={t('common:action.clear')}
          disabled={disabled}
          displayValue={anchorDisplayValue}
          hasClear={hasClear}
          hasError={hasError}
          id={id}
          isoValue={isoValue}
          max={maxIsoValue}
          min={minIsoValue}
          onClear={
            hasClear
              ? () => {
                  onChange(undefined)
                }
              : undefined
          }
          onNativeChange={(raw) => {
            // raw `<input type="date">` は空文字をそのまま onChange へ渡していた
            // (旧挙動)。 `hasClear` は × ボタンの表示可否のみを制御するため、
            // ここでは常に呼ぶ — 空値を無視するかどうかは呼び出し側の責務。
            const parsed = parseDateInputValue(raw)
            // native `max` 属性はネイティブ picker の範囲を絞るだけで、
            // キーボード直接入力の change イベント自体は超過値でも発火する
            // (ブラウザ実挙動)。JS 側で max にクランプしないと未来日ガードが
            // 手入力経路だけ抜ける。
            // `min` も同じ理由 (native `min` 属性はキーボード直接入力の change
            // イベントを止めない) で JS 側にクランプを二重化する。
            const clamped =
              parsed !== undefined &&
              max !== undefined &&
              compareCivilDate(parsed, max) > 0
                ? max
                : parsed !== undefined &&
                    min !== undefined &&
                    compareCivilDate(parsed, min) < 0
                  ? min
                  : parsed
            if (
              clamped !== undefined &&
              dayState?.(civilDateToJsDate(clamped))?.disabled === true
            )
              return
            onChange(clamped)
          }}
          placeholder={placeholder}
        />
      }
      dayState={dayState}
      disabled={disabled}
      isSelectedLabelPlaceholder={displayValue === ''}
      maxDate={maxJsDate}
      minDate={minJsDate}
      onChange={(d) => {
        onChange(jsDateToCivilDate(d))
      }}
      onViewMonthChange={onViewMonthChange}
      selectedLabel={displayValue === '' ? placeholder : displayValue}
      singularDaysByDateKey={singularDaysByDateKey}
      value={jsDate}
    />
  )
}
